import { Link } from "react-router-dom";
import {
  Sparkles,
  HeartPulse,
  Brain,
  Users,
  Sprout,
  ArrowUpRight,
  CheckCircle2,
  Stethoscope,
  Apple,
  HandHeart,
  Leaf,
  MessageCircleHeart,
  HeartHandshake,
} from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { BlobShape, FloralAccent, LeafBranch } from "../components/Organic";
import { AUDIENCES, FOCUS_AREAS, PARTNERS } from "../lib/content";

const ICONS = {
  HeartPulse,
  Brain,
  Users,
  Sprout,
  Stethoscope,
  Apple,
  HandHeart,
  Leaf,
  MessageCircleHeart,
  HeartHandshake,
};

export default function Home() {
  return (
    <div data-testid="page-home">
      <Hero />
      <Welcome />
      <WhoISupport />
      <FocusPreview />
      <HolisticTeaser />
      <CtaBand />
    </div>
  );
}

function Hero() {
  return (
    <section
      data-testid="home-hero"
      className="relative pt-44 md:pt-52 pb-20 md:pb-28 overflow-hidden"
    >
      <BlobShape
        className="absolute -top-32 -right-32 w-[640px] h-[640px]"
        color="#8DAA91"
        opacity={0.18}
      />
      <BlobShape
        className="absolute -bottom-44 -left-32 w-[520px] h-[520px]"
        color="#E4BE9E"
        opacity={0.22}
      />
      <FloralAccent
        className="absolute top-24 left-[42%] w-40 h-40 hidden md:block"
        color="#2A7B76"
        opacity={0.12}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <span className="inline-flex items-center gap-2 overline bg-[var(--at-teal-tint)] px-4 py-1.5 rounded-full">
              <Sparkles size={14} /> Neurodiversity-Affirming Practice
            </span>
          </Reveal>
          <Reveal delay={1}>
            <h1 className="h-display mt-6 text-[2.6rem] sm:text-5xl md:text-6xl lg:text-[4.4rem]">
              A space to be{" "}
              <span className="h-italic-accent">heard, understood,</span>{" "}
              <br className="hidden md:block" />
              and empowered.
            </h1>
          </Reveal>
          <Reveal delay={2}>
            <div className="mt-7 space-y-5 text-base md:text-lg leading-relaxed text-[var(--at-navy-soft)] max-w-2xl">
              <p>
                Taking the first step toward counseling can feel
                overwhelming — but you don't have to navigate life's
                challenges alone.{" "}
                <span className="font-semibold text-[var(--at-navy)]">
                  All Thrive Therapy and Consulting, PLLC
                </span>{" "}
                provides a warm, supportive, and nonjudgmental space where
                children, teens, adults, and caregivers can explore their
                concerns, build on their strengths, and work toward meaningful
                change.
              </p>
              <p>
                Neurodiversity-affirming, person-centered, and evidence-based
                care for big emotions, anxiety, depression, ADHD, and life's
                unique paths.
              </p>
            </div>
          </Reveal>
          <Reveal delay={3}>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-consultation"
                className="btn-primary"
              >
                Schedule a Free Consultation <ArrowUpRight size={18} />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="btn-outline"
              >
                Explore My Services
              </Link>
            </div>
          </Reveal>
          <Reveal delay={4}>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[var(--at-navy-soft)]">
              {[
                "Telehealth + In-Person",
                "All Ages Welcome",
                "Neurodiversity-Affirming",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[var(--at-teal)]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5 relative">
          <Reveal delay={2}>
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-40 h-40 bg-[var(--at-olive)]/30 blob" />
              <div className="absolute -bottom-8 -right-6 w-44 h-44 bg-[var(--at-sand)]/40 blob-2" />
              <img
                src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?crop=entropy&cs=srgb&fm=jpg&w=1100&q=85"
                alt="Two people in a warm, empathetic conversation"
                className="relative w-full h-[460px] md:h-[540px] object-cover mask-organic-1 shadow-[0_30px_60px_-30px_rgba(26,46,68,0.35)]"
              />
              <div className="absolute -bottom-6 -left-6 bg-[var(--at-bg)] rounded-2xl px-5 py-4 shadow-xl border border-[var(--at-border)] max-w-[230px]">
                <p className="text-[0.7rem] uppercase tracking-[0.18em] text-[var(--at-teal)] font-semibold">
                  Whole-person care
                </p>
                <p className="mt-1 font-serif text-[var(--at-navy)] text-base leading-snug">
                  You're not a list of symptoms. You're a person worth knowing.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Welcome() {
  return (
    <section
      data-testid="home-welcome"
      className="relative py-24 md:py-32 bg-[var(--at-bg-2)] overflow-hidden noise"
    >
      <LeafBranch
        className="absolute top-12 right-6 w-40 h-40 hidden md:block"
        color="#8DAA91"
        opacity={0.4}
      />
      <LeafBranch
        className="absolute bottom-12 left-6 w-32 h-32 hidden md:block -scale-x-100"
        color="#2A7B76"
        opacity={0.3}
      />
      <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
        <Reveal>
          <span className="overline">Compassionate, Person-Centered Therapy</span>
        </Reveal>
        <Reveal delay={1}>
          <h2 className="h-display mt-5 text-3xl md:text-5xl">
            Building resilience,{" "}
            <span className="h-italic-accent">connection,</span> and growth.
          </h2>
        </Reveal>
        <Reveal delay={2}>
          <div className="mt-8 space-y-5 text-lg leading-relaxed text-[var(--at-navy-soft)]">
            <p>
              I support children, teens, adults, and caregivers facing a wide
              range of emotional, behavioral, relational, and life-transition
              concerns. Whether you're seeking therapy for yourself, your
              child, or your family, you're welcomed exactly as you are.
            </p>
            <p>
              Reaching out for support isn't weakness — it's courage. And it's
              the first step toward reclaiming your well-being.
            </p>
            <p className="font-serif text-2xl md:text-3xl text-[var(--at-navy)] mt-10 leading-snug">
              You aren't alone — and you don't have to figure this out in
              isolation.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function WhoISupport() {
  return (
    <section
      id="who-i-support"
      data-testid="home-audiences"
      className="py-24 md:py-32 px-6 md:px-10"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <Reveal>
            <div className="max-w-2xl">
              <span className="overline">Who I Support</span>
              <h2 className="h-display mt-4 text-4xl md:text-5xl">
                Care that meets you{" "}
                <span className="h-italic-accent">exactly where you are.</span>
              </h2>
            </div>
          </Reveal>
          <Reveal delay={1}>
            <p className="text-[var(--at-navy-soft)] max-w-md text-base">
              From sensitive children to overwhelmed caregivers, every person
              who walks through my (virtual) door is welcomed without
              judgment.
            </p>
          </Reveal>
        </div>

        <StaggerGroup className="grid grid-cols-1 md:grid-cols-6 auto-rows-[minmax(260px,_auto)] gap-5 md:gap-6">
          {AUDIENCES.map((a, i) => (
            <StaggerItem
              key={a.id}
              className={
                i === 0
                  ? "md:col-span-4"
                  : i === 3
                    ? "md:col-span-4"
                    : "md:col-span-2"
              }
            >
              <AudienceCard data={a} large={i === 0 || i === 3} />
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

function AudienceCard({ data, large }) {
  const accentMap = {
    olive: "bg-[var(--at-olive)]/20 text-[var(--at-navy)]",
    teal: "bg-[var(--at-teal-tint)] text-[var(--at-teal)]",
    sand: "bg-[var(--at-sand)]/30 text-[var(--at-navy)]",
    navy: "bg-[var(--at-navy)]/10 text-[var(--at-navy)]",
  };
  return (
    <div
      data-testid={`audience-card-${data.id}`}
      className="bento-card group relative h-full rounded-[2rem] overflow-hidden border border-[var(--at-border)] bg-[var(--at-bg)]"
    >
      <div
        className={`relative ${large ? "h-56" : "h-40"} overflow-hidden`}
      >
        <img
          src={data.image}
          alt={data.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--at-navy)]/40 to-transparent" />
      </div>
      <div className="p-6 md:p-7">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-serif text-xl md:text-2xl text-[var(--at-navy)] leading-snug">
            {data.title}
          </h3>
          <span
            className={`shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[0.7rem] font-semibold uppercase tracking-wider ${accentMap[data.accent]}`}
          >
            Care
          </span>
        </div>
        <p className="mt-3 text-[var(--at-navy-soft)] leading-relaxed">
          {data.description}
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {data.tags.map((t) => (
            <span
              key={t}
              className="text-xs px-3 py-1 rounded-full bg-[var(--at-bg-2)] text-[var(--at-navy-soft)] border border-[var(--at-border)]"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function FocusPreview() {
  return (
    <section
      data-testid="home-focus"
      className="relative py-24 md:py-32 px-6 md:px-10 bg-[var(--at-bg-2)] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <Reveal>
            <span className="overline">Areas of Focus</span>
            <h2 className="h-display mt-4 text-4xl md:text-5xl">
              Evidence-based tools,{" "}
              <span className="h-italic-accent">tailored to your life.</span>
            </h2>
            <p className="mt-6 text-lg text-[var(--at-navy-soft)] leading-relaxed">
              I combine compassionate, non-judgmental listening with proven,
              practical strategies to help you understand your emotions, build
              sustainable coping skills, and reduce daily stress.
            </p>
            <Link
              to="/services"
              data-testid="home-focus-cta"
              className="btn-primary mt-8"
            >
              See All Services <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
        <StaggerGroup className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {FOCUS_AREAS.map((f) => {
            const Icon = ICONS[f.icon] || HeartPulse;
            return (
              <StaggerItem key={f.title}>
                <div
                  data-testid={`focus-card-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="bento-card h-full rounded-[1.75rem] border border-[var(--at-border)] bg-[var(--at-bg)] p-7"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[var(--at-teal-tint)] flex items-center justify-center text-[var(--at-teal)]">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-5 font-serif text-xl text-[var(--at-navy)] leading-snug">
                    {f.title}
                  </h3>
                  <p className="mt-3 text-[var(--at-navy-soft)] leading-relaxed">
                    {f.body}
                  </p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
    </section>
  );
}

function HolisticTeaser() {
  return (
    <section className="py-24 md:py-32 px-6 md:px-10" data-testid="home-holistic-teaser">
      <div className="max-w-7xl mx-auto relative rounded-[2.5rem] bg-[var(--at-navy)] text-[var(--at-bg)] px-8 md:px-16 py-16 md:py-24 overflow-hidden">
        <BlobShape
          className="absolute -top-20 -right-24 w-[420px] h-[420px]"
          color="#2A7B76"
          opacity={0.28}
        />
        <BlobShape
          className="absolute -bottom-32 -left-20 w-[360px] h-[360px]"
          color="#8DAA91"
          opacity={0.22}
        />
        <LeafBranch
          className="absolute top-8 right-12 w-32 h-32 hidden md:block"
          color="#8DAA91"
          opacity={0.55}
        />
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="overline text-[var(--at-olive)]">
                🤝 Holistic Care
              </span>
              <h2 className="h-display mt-4 text-4xl md:text-5xl text-[var(--at-bg)]">
                My collaborative{" "}
                <span className="italic text-[var(--at-olive)]">network</span> of
                wellness partners.
              </h2>
              <p className="mt-6 text-lg text-[var(--at-bg)]/80 leading-relaxed max-w-2xl">
                True wellness requires looking at the whole picture. I partner
                with trusted specialists — medical, nutritional, occupational,
                and holistic — so your care is integrated, comprehensive, and
                unified.
              </p>
              <Link
                to="/services#holistic-care"
                data-testid="home-holistic-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold bg-[var(--at-olive)] text-[var(--at-navy)] hover:bg-[var(--at-bg)] transition-colors"
              >
                Meet the Network <ArrowUpRight size={18} />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <StaggerGroup className="grid grid-cols-2 gap-3">
              {PARTNERS.map((p) => {
                const Icon = ICONS[p.icon] || Leaf;
                return (
                  <StaggerItem key={p.name}>
                    <div className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm">
                      <div className="w-10 h-10 rounded-xl bg-[var(--at-olive)]/25 flex items-center justify-center text-[var(--at-olive)]">
                        <Icon size={18} />
                      </div>
                      <p className="mt-3 text-sm text-[var(--at-bg)]/90 leading-snug">
                        {p.name}
                      </p>
                    </div>
                  </StaggerItem>
                );
              })}
            </StaggerGroup>
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section
      data-testid="home-cta-band"
      className="relative px-6 md:px-10 pb-24"
    >
      <div className="max-w-5xl mx-auto rounded-[2.5rem] bg-[var(--at-bg-2)] border border-[var(--at-border)] p-10 md:p-16 text-center relative overflow-hidden noise">
        <FloralAccent
          className="absolute -top-12 left-1/2 -translate-x-1/2 w-32 h-32"
          color="#2A7B76"
          opacity={0.18}
        />
        <Reveal>
          <h2 className="h-display text-3xl md:text-5xl">
            Let's build a path forward,{" "}
            <span className="h-italic-accent">together.</span>
          </h2>
          <p className="mt-5 text-lg text-[var(--at-navy-soft)] max-w-2xl mx-auto">
            Taking the first step can feel daunting. You don't have to walk it
            alone — I'm here to hold space and help you thrive.
          </p>
          <Link
            to="/contact"
            data-testid="home-bottom-cta"
            className="btn-primary mt-9"
          >
            Schedule a Free Consultation <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
